// apps/web/src/lib/flashcardMasteryPolicy.ts
// ─────────────────────────────────────────────────────────────────────────
// THE SINGLE FLASHCARD MASTERY POLICY (web layer). One file. One source of
// truth. Every surface — the review session, the manager, the lesson-page
// Memory Anchors block, module progress, badge evaluation, and the reward
// dispatcher conditions — consumes THIS. Nothing re-derives the floor or the
// "solid" rule on its own. (Flashcard spec §16.)
//
// ── Two distinct tiers (spec §5) ───────────────────────────────────────────
//   • CHECKPOINT (short-term proof) → gates LESSON PROGRESS.
//       A card is "solid" when it's active, has been reviewed at least once,
//       and its LATEST rating was Remembered (good) or Easy. No 21-day wait,
//       no rep count. This is "I understood the anchor just now."
//   • RETAINED (long-term) → badges, review-health. The pure engine's
//       isMastered (≥4 reps, ≥21-day interval, ≤1 lapse). Surfaced here as the
//       top rung of the memory ladder ("Retained"), never as the progress gate.
//
// The pure SRS engine stays oblivious to lessons, prefs, and roles. This layer
// owns the product policy on top of it.
// ─────────────────────────────────────────────────────────────────────────

import {
	FLASHCARD_ROLES,
	type Flashcard,
	type FlashcardRole,
} from '@nisk/core/learning';
import { isMastered, type CardSRSRecord } from '@nisk/core/srs';
import type { UserFlashcardPreference } from './flashcardPrefs';

// ── User-facing memory ladder (spec §6) ────────────────────────────────────
// Never expose raw SRS state names to users. This is the only vocabulary the
// UI should speak when describing a single card's memory strength.
//
// IMPORTANT terminology rule (product): "Getting stronger" means the card is
// good enough for the LESSON CHECKPOINT (short-term). "Retained" means the
// card is remembered ACROSS TIME (the engine's isMastered: ≥4 reps, ≥21-day
// interval). We deliberately do NOT use "Solid memory" / "Mastered" as a
// formal user label — it blurs checkpoint completion with long-term retention.
export type CardMemoryState =
	| 'new' // not reviewed yet
	| 'learning' // still shaky
	| 'reviewing' // coming back later
	| 'strong' // getting stronger (checkpoint-solid)
	| 'mastered'; // RETAINED — long-term memory (engine isMastered)

export const CARD_MEMORY_STATE_COPY: Record<CardMemoryState, string> = {
	new: 'Not reviewed yet',
	learning: 'Still shaky',
	reviewing: 'Coming back later',
	strong: 'Getting stronger',
	mastered: 'Retained',
};

// Hover/explainer copy for each rung. Centralized HERE so every surface — the
// lesson block, the manager, the review landing — speaks one definition. The
// "keep reviewing to retain it" line on 'strong' is load-bearing: it teaches
// the difference between short-term checkpoint and long-term retention.
export const CARD_MEMORY_STATE_HELP: Record<CardMemoryState, string> = {
	new: 'You haven’t studied this card yet. It will appear in your next review.',
	learning:
		'You forgot or barely remembered this. It will come back soon.',
	reviewing: 'This card is scheduled for a future review.',
	strong:
		'You remembered this well enough for the lesson checkpoint. Keep reviewing to retain it.',
	mastered: 'You’ve remembered this across multiple reviews over time.',
};

// ── Role helpers ────────────────────────────────────────────────────────────
// Legacy cards may lack a role. Fall back by position (0→main_idea,
// 1→watch_out, 2→use_it) so the manager can still group them and the UI never
// shows a blank label. New content always carries an explicit role.
export function resolveCardRole(
	card: Flashcard,
	indexInLesson: number,
): FlashcardRole {
	if (card.role) return card.role;
	return FLASHCARD_ROLES[indexInLesson] ?? 'main_idea';
}

// ── Active-card floor (spec §5, §22) ────────────────────────────────────────
// With the 3-card model the floor is simple: keep at least 2 of 3 on. Legacy
// fallbacks: 2 cards → 1, 1 card → 1, 0 → 0. A user can turn off one of three
// anchors, never two.
export function getMinimumActiveFlashcards(totalCoreCards: number): number {
	if (totalCoreCards <= 0) return 0;
	if (totalCoreCards >= 3) return 2;
	return 1;
}

export function getActiveFlashcards(
	cards: Flashcard[],
	preferencesByCardId: Record<string, UserFlashcardPreference | undefined>,
): Flashcard[] {
	return cards.filter(
		(card) => preferencesByCardId[card.id]?.isActive !== false,
	);
}

/**
 * Can this card be turned OFF right now without dropping below the floor?
 * Turning a card ON is always allowed; this only guards deactivation.
 */
export function canDeactivateFlashcard(
	cards: Flashcard[],
	preferencesByCardId: Record<string, UserFlashcardPreference | undefined>,
	cardId: string,
): boolean {
	const pref = preferencesByCardId[cardId];
	const isCurrentlyActive = pref?.isActive !== false;
	if (!isCurrentlyActive) return true; // already off — toggling on is fine
	const active = getActiveFlashcards(cards, preferencesByCardId);
	const floor = getMinimumActiveFlashcards(cards.length);
	return active.length > floor;
}

// ── Latest-rating signal ────────────────────────────────────────────────────
// The checkpoint "solid" test reads the most recent rating off reviewHistory.
function latestRating(record: CardSRSRecord | undefined): string | null {
	if (!record || record.reviewHistory.length === 0) return null;
	return record.reviewHistory[record.reviewHistory.length - 1].rating;
}

function hasBeenReviewed(record: CardSRSRecord | undefined): boolean {
	return Boolean(record && record.reviewHistory.length > 0);
}

/**
 * Is this card SOLID for checkpoint purposes? (spec §5 "What 'solid' means")
 *   • reviewed at least once;
 *   • latest rating is Remembered (good) or Easy;
 *   • (active-ness is enforced by the caller via getActiveFlashcards).
 * No 21-day wait. No rep requirement. This is short-term understanding.
 */
export function isCardSolid(record: CardSRSRecord | undefined): boolean {
	const rating = latestRating(record);
	return rating === 'good' || rating === 'easy';
}

// ── User-facing memory state for a single card (spec §6) ────────────────────
export function getCardMemoryState(
	record: CardSRSRecord | undefined,
): CardMemoryState {
	if (!hasBeenReviewed(record)) return 'new';
	// Long-term retention wins the top rung.
	if (record && isMastered(record)) return 'mastered';
	if (isCardSolid(record)) return 'strong';
	// Reviewed, but last answer wasn't solid. Distinguish "coming back later"
	// (graduated/scheduled out) from "still shaky" (in a learning loop).
	if (record && (record.state === 'review' || record.state === 'mastered')) {
		return 'reviewing';
	}
	return 'learning';
}

// ── Lesson-level summary (the one object every surface reads) ───────────────
export interface LessonCardState {
	cardId: string;
	role: FlashcardRole;
	isActive: boolean;
	memoryState: CardMemoryState;
	hasAnswerOverride: boolean;
	reviewed: boolean;
	solid: boolean;
}

export interface LessonFlashcardMasterySummary {
	totalCoreCards: number;
	activeCards: number;
	minimumActiveCards: number;
	reviewedActiveCards: number;
	solidActiveCards: number; // checkpoint-solid (short-term)
	retainedActiveCards: number; // engine isMastered (long-term)
	/** Lesson PROGRESS gate: ≥floor solid AND every active card reviewed once. */
	checkpointPassed: boolean;
	/** All active cards solid (the 3/3 bonus state). */
	perfectMemory: boolean;
	cardStates: LessonCardState[];
}

export function getLessonFlashcardMasterySummary(params: {
	flashcards: Flashcard[];
	preferencesByCardId: Record<string, UserFlashcardPreference | undefined>;
	srsStateByCardId: Record<string, CardSRSRecord | undefined>;
}): LessonFlashcardMasterySummary {
	const { flashcards, preferencesByCardId, srsStateByCardId } = params;

	const cardStates: LessonCardState[] = flashcards.map((card, index) => {
		const pref = preferencesByCardId[card.id];
		const record = srsStateByCardId[card.id];
		return {
			cardId: card.id,
			role: resolveCardRole(card, index),
			isActive: pref?.isActive !== false,
			memoryState: getCardMemoryState(record),
			hasAnswerOverride: Boolean(pref?.customBack || pref?.customFront),
			reviewed: hasBeenReviewed(record),
			solid: isCardSolid(record),
		};
	});

	const active = cardStates.filter((c) => c.isActive);
	const reviewedActive = active.filter((c) => c.reviewed).length;
	const solidActive = active.filter((c) => c.solid).length;
	const retainedActive = active.filter(
		(c) => c.memoryState === 'mastered',
	).length;

	const minimumActiveCards = getMinimumActiveFlashcards(flashcards.length);

	// Checkpoint (spec §5 final rule): every active card reviewed at least
	// once AND ≥ floor active cards currently solid AND the floor itself is met.
	const meetsFloor = active.length >= minimumActiveCards && active.length > 0;
	const allActiveReviewed = active.length > 0 && reviewedActive === active.length;
	const checkpointPassed =
		meetsFloor && allActiveReviewed && solidActive >= minimumActiveCards;

	// Perfect: every active card solid (and floor met).
	const perfectMemory =
		meetsFloor && active.length > 0 && solidActive === active.length;

	return {
		totalCoreCards: flashcards.length,
		activeCards: active.length,
		minimumActiveCards,
		reviewedActiveCards: reviewedActive,
		solidActiveCards: solidActive,
		retainedActiveCards: retainedActive,
		checkpointPassed,
		perfectMemory,
		cardStates,
	};
}

// ── Convenience predicates (read off the summary) ───────────────────────────
export function isLessonFlashcardCheckpointPassed(params: {
	flashcards: Flashcard[];
	preferencesByCardId: Record<string, UserFlashcardPreference | undefined>;
	srsStateByCardId: Record<string, CardSRSRecord | undefined>;
}): boolean {
	return getLessonFlashcardMasterySummary(params).checkpointPassed;
}

